import React, { useEffect, useMemo, useState } from "react";
import { useTable } from "@/lib/store";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card, PageHead, Chip, Empty, Kicker, Avatar, Pick } from "@/components/kit";
import { DateField } from "@/components/DateField";
import { ROMAN, fmtDate } from "@/lib/util";
import { Check, Minus, X, CalendarCheck, Trash2, ChevronDown } from "lucide-react";

const ANSWERS = [
  ["yes", "Yes", "var(--green)", "var(--green-wash)", Check],
  ["maybe", "Maybe", "var(--amber)", "var(--amber-wash)", Minus],
  ["no", "No", "var(--rust)", "var(--rust-wash)", X],
];

/* One big row per date: the date, and three buttons. That is the whole idea. */
const DateRow = ({ option, mine, canAnswer, onAnswer, isAdmin, onConfirm, onDelete }) => {
  const locked = option.status === "Confirmed";
  return (
    <Card className="p-5" data-testid={`finder-option-${option.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5">
        <div className="min-w-0 flex-1">
          <div className="serif text-2xl leading-tight whitespace-nowrap" data-testid={`finder-date-label-${option.id}`}>
            {fmtDate(option.date)}
          </div>
          <div className="text-[12.5px] mt-1.5 flex gap-2 flex-wrap items-center" style={{ color: "var(--ink-mute)" }}>
            {locked ? (
              <Chip color="var(--green)">This is the date</Chip>
            ) : (
              <>
                <span style={{ color: "var(--green)" }}>{option.stats.yes} yes</span>
                <span style={{ color: "var(--amber)" }}>{option.stats.maybe} maybe</span>
                <span style={{ color: "var(--rust)" }}>{option.stats.no} no</span>
                {option.stats.pending > 0 && <span>{option.stats.pending} yet to answer</span>}
                {option.stats.allClear && (
                  <span style={{ color: "var(--green)" }}>· everyone is clear</span>
                )}
              </>
            )}
          </div>
        </div>

        {!locked && (
          <div className="flex gap-2 shrink-0">
            {ANSWERS.map(([key, label, color, wash, Icon]) => {
              const on = mine === key;
              return (
                <button
                  key={key}
                  disabled={!canAnswer}
                  data-testid={`finder-answer-${option.id}-${key}`}
                  onClick={() => onAnswer(key)}
                  className="inline-flex flex-1 sm:flex-none items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-[13px] font-medium"
                  style={{
                    background: on ? color : wash,
                    color: on ? "#fff" : color,
                    border: `1px solid ${color}`,
                    opacity: canAnswer ? 1 : 0.45,
                    cursor: canAnswer ? "pointer" : "default",
                    transition: "background-color .2s var(--ease), color .2s var(--ease)",
                  }}
                >
                  <Icon size={14} />
                  {label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {isAdmin && !locked && (
        <div className="flex items-center gap-3 mt-5 pt-4" style={{ borderTop: "1px solid var(--hair)" }}>
          <button className="btn-ink !min-h-0 !py-2.5" data-testid={`finder-confirm-${option.id}`} onClick={onConfirm}>
            <CalendarCheck size={14} /> Lock this date
          </button>
          <button className="btn-quiet !min-h-0 !py-2.5" data-testid={`finder-delete-${option.id}`} onClick={onDelete}>
            <Trash2 size={13} /> Remove
          </button>
        </div>
      )}
    </Card>
  );
};

export default function DateFinder() {
  const { me, meetings, activeMembers, canWrite, refresh } = useTable();
  const [meetingId, setMeetingId] = useState("");
  const [data, setData] = useState({ options: [] });
  const [dates, setDates] = useState(["", "", ""]);
  const [showAll, setShowAll] = useState(false);
  const isAdmin = me.isAdmin && canWrite;

  const openChapters = useMemo(() => meetings.filter((m) => m.status !== "Done"), [meetings]);

  useEffect(() => {
    if (!meetingId && openChapters.length) setMeetingId(openChapters[0].id);
  }, [openChapters, meetingId]);

  const load = async (id) => {
    if (!id) return;
    setData((await api.get("/finder", { params: { meetingId: id } })).data);
  };
  useEffect(() => { load(meetingId); /* eslint-disable-next-line */ }, [meetingId]);

  const meeting = meetings.find((m) => m.id === meetingId);
  const options = (data.options || []).slice().sort((a, b) => b.stats.score - a.stats.score);
  const confirmed = options.find((o) => o.status === "Confirmed");
  const myPending = options.filter((o) => o.status === "Open" && !(o.votes || {})[me.memberId]).length;

  const answer = async (oid, value) => {
    try {
      setData((await api.put(`/finder/${oid}/availability`, { answer: value })).data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const propose = async () => {
    const list = dates.filter(Boolean);
    if (!list.length) { toast.error("Add at least one date."); return; }
    try {
      setData((await api.post("/finder/options", { meetingId, dates: list })).data);
      setDates(["", "", ""]);
      toast.success("Dates are on the table. Everyone can answer now.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const confirm = async (oid) => {
    try {
      setData((await api.post(`/finder/${oid}/confirm`)).data);
      await refresh();
      toast.success("Date locked.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="date-finder-page">
      <PageHead
        kicker={meeting ? `Chapter ${ROMAN[meeting.num - 1]}` : "Scheduling"}
        title={confirmed ? "The date is set" : "Pick the next date"}
        sub={
          confirmed
            ? `${fmtDate(confirmed.date)} — nothing else to do here.`
            : myPending
              ? "Tap Yes, Maybe or No against each date. That is all."
              : isAdmin
                ? "Put dates on the table, then lock the one that works."
                : "You have answered. We are waiting on the rest of the table."
        }
        right={
          openChapters.length > 1 ? (
            <div className="min-w-[200px]">
              <Pick
                label="Chapter"
                data-testid="finder-chapter"
                value={meetingId}
                onChange={(e) => setMeetingId(e.target.value)}
                options={openChapters.map((m) => ({
                  value: m.id,
                  label: `Chapter ${ROMAN[m.num - 1]}`,
                }))}
              />
            </div>
          ) : null
        }
      />

      {isAdmin && !confirmed && (
        <Card className="mb-6 p-5" data-testid="finder-propose">
          <Kicker>Put dates on the table</Kicker>
          <div className="grid sm:grid-cols-3 gap-3 mt-4">
            {dates.map((d, i) => (
              <DateField
                key={i}
                value={d}
                testId={`finder-date-${i}`}
                placeholder={`Date ${i + 1}`}
                onChange={(v) => setDates(dates.map((x, j) => (j === i ? v : x)))}
              />
            ))}
          </div>
          <button className="btn-ink mt-4" data-testid="finder-propose-save" onClick={propose}>
            Ask the table
          </button>
        </Card>
      )}

      {options.length === 0 ? (
        <Empty
          line={isAdmin ? "No dates yet. Put two or three on the table above." : "No dates yet. The moderator will put some up."}
          testId="finder-empty"
        />
      ) : (
        <div className="space-y-3 stagger">
          {options.map((o) => (
            <DateRow
              key={o.id}
              option={o}
              mine={(o.votes || {})[me.memberId]}
              canAnswer={canWrite && o.status === "Open"}
              onAnswer={(v) => answer(o.id, v)}
              isAdmin={isAdmin}
              onConfirm={() => confirm(o.id)}
              onDelete={async () => setData((await api.delete(`/finder/${o.id}`)).data)}
            />
          ))}
        </div>
      )}

      {options.length > 0 && (
        <div className="mt-6">
          <button
            className="btn-quiet"
            data-testid="finder-toggle-everyone"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? "Hide" : "See"} everyone's answers
            <ChevronDown size={13} style={{ transform: showAll ? "rotate(180deg)" : "none", transition: "transform .2s var(--ease)" }} />
          </button>
          {showAll && (
            <Card className="mt-4 p-5" data-testid="finder-everyone">
              <div className="space-y-3">
                {activeMembers.map((m) => (
                  <div key={m.id} className="flex items-center gap-3 flex-wrap" data-testid={`finder-row-${m.id}`}>
                    <Avatar name={m.name} color={m.avatarColor} size={24} />
                    <span className="text-[13px] min-w-[84px]">{m.name.split(" ")[0]}</span>
                    {options.map((o) => {
                      const a = ANSWERS.find((x) => x[0] === (o.votes || {})[m.id]);
                      return (
                        <span
                          key={o.id}
                          data-testid={`finder-cell-${o.id}-${m.id}`}
                          className="text-[11.5px] rounded-full px-2.5 py-1"
                          style={{
                            background: a ? a[3] : "var(--paper-deep)",
                            border: `1px solid ${a ? a[2] : "var(--hair)"}`,
                            color: a ? a[2] : "var(--ink-faint)",
                          }}
                        >
                          {new Date(`${o.date}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                          {a ? ` · ${a[1]}` : " · —"}
                        </span>
                      );
                    })}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
